import { EventEmitter, Injectable } from '@angular/core';

// eslint-disable-next-line import/no-extraneous-dependencies
import Pubnub from 'pubnub';

import { Logger } from '@sondermind/datadog-logging';
import { SentryClient } from '@sondermind/sentry-client';

import { PubnubError } from './pubnub-lib-error';
import { Statuses, parseStatus } from './pubnub-lib-statuses';
import { Bootstrap } from './pubnub-lib.interface';
import { pubnubFactory } from './pubnub-lib.module';

@Injectable({
  providedIn: 'root'
})
export class PubnubLib {
  pubnub!: Pubnub;
  channels: Set<string> = new Set();

  statuses$ = new EventEmitter<Statuses | null>();
  errors$ = new EventEmitter<PubnubError>();

  constructor(
    public logger: Logger
  ) {}

  initializePubnub(data: Bootstrap): void {
    // https://www.pubnub.com/docs/sdks/javascript/api-reference/configuration
    const config: Pubnub.PubnubConfig = {
      publishKey: data.pubnub_publish_key,
      subscribeKey: data.pubnub_subscribe_key,
      uuid: data.profile_uuid,

      // networking
      ssl: true,
      keepAlive: true,

      // allow catch up on the front-end applications
      restore: true,

      // announce when the network is down or up
      autoNetworkDetection: true,

      // since we don't use the Presence API, don't send out leave requests
      suppressLeaveEvents: true,
    };

    const pubnub = pubnubFactory(config);
    pubnub.setToken(data.pubnub_auth_token);
    this.pubnub = pubnub;
  }

  // add the given channels to the list we're subscribed to
  // if the connection is initialized, executes the subscriptions
  connect(...channels: string[]): void {
    channels.forEach((c) => this.channels.add(c));
  }

  // handle incoming status message
  // these may be errors (PubnubStatus) or normal status events
  incomingStatusEvent(status: Pubnub.StatusEvent | Pubnub.PubnubStatus): void {
    this.isPubnubError(status) ? this.incomingError(status) : this.incomingStatus(status);
  }

  // type guard, StatusEvent (normal status messages) vs. PubnubStatus (error return from async call)
  isPubnubError(status: Pubnub.StatusEvent | Pubnub.PubnubStatus): status is Pubnub.PubnubStatus {
    return (status as Pubnub.PubnubStatus).error != null;
  }

  // handle incoming non-error statuses
  // right now we just push everything out into the status stream
  incomingStatus(event: Pubnub.StatusEvent): void {
    const status = parseStatus(event);
    this.statuses$.next(status);
  }

  // handle incoming errors
  // when an async call fails, we get the failure through the status callback
  incomingError(status: Pubnub.PubnubStatus): void {
    if (status.error) {
      this.errors$.next(status);

      const statusMessage = status.errorData?.message || 'Unknown error message';
      const errorMessage = `${status.operation}: ${statusMessage} (${status.category})`;

      this.logger.addError(errorMessage, status);
      SentryClient.captureException(errorMessage, { extra: { status } });
    } else {
      // eslint-disable-next-line no-console
      console.log('got a status error event that\'s not an error', status);
    }
  }
}
